import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTrash, faPlus, faEdit, faImage, faSave, faTimes, 
    faChartLine, faCheckCircle, faCloudUploadAlt 
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from '../apiConfig';

const AdminStandardSection = ({ tab, list, refresh }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({});
    const [file, setFile] = useState(null);
    const [editId, setEditId] = useState(null);
    const [loading, setLoading] = useState(false);

    const getHeaders = useCallback((isMultipart = false) => {
        const headers = {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'ngrok-skip-browser-warning': 'true'
        };
        if (!isMultipart) {
            headers['Content-Type'] = 'application/json';
        }
        return headers;
    }, []);

    useEffect(() => {
        setFormData({});
        setFile(null);
        setEditId(null);
    }, [tab]);

    // Դինամիկ վերնագրերի ստացում ըստ tab-ի
    const getTabName = () => {
        switch(tab) {
            case 'slides': return t('tab_slides');
            case 'categories': return t('tab_categories');
            case 'partners': return t('tab_partners');
            default: return t('tab_posts');
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const data = new FormData();
            Object.keys(formData).forEach(key => {
                if (key !== 'image' && key !== 'logo' && formData[key] !== null) {
                    data.append(key, formData[key]);
                }
            });

            if (file) data.append('image', file);

            const method = editId ? 'PUT' : 'POST';
            const url = editId ? `${API_BASE}/api/${tab}/${editId}` : `${API_BASE}/api/${tab}`;

            const res = await fetch(url, { 
                method, 
                body: data,
                headers: getHeaders(true)
            });

            if (res.ok) {
                setFormData({});
                setFile(null);
                setEditId(null);
                refresh();
            }
        } catch (err) {
            console.error("Save error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (item) => {
        setEditId(item.id);
        setFormData({ ...item });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('confirm_delete'))) return;
        try {
            const res = await fetch(`${API_BASE}/api/${tab}/${id}`, { 
                method: 'DELETE',
                headers: getHeaders()
            });
            if (res.ok) refresh();
        } catch (err) { console.error(err); }
    };

    return (
        <div style={styles.container}>
            <div style={styles.analyticsRow}>
                <div style={{...styles.anaCard, background: 'linear-gradient(135deg, #4318FF 0%, #5E37FF 100%)'}}>
                    <div style={styles.anaIconBox}><FontAwesomeIcon icon={faChartLine} /></div>
                    <div>
                        <p style={styles.anaLabel}>{t('total_label')} {getTabName()}</p>
                        <h3 style={styles.anaVal}>{list.length} {t('unit_count')}</h3>
                    </div>
                </div>
                <div style={{...styles.anaCard, background: 'linear-gradient(135deg, #868CFF 0%, #4318FF 100%)'}}>
                    <div style={styles.anaIconBox}><FontAwesomeIcon icon={faCheckCircle} /></div>
                    <div>
                        <p style={styles.anaLabel}>{t('status_label')}</p>
                        <h3 style={styles.anaVal}>{t('status_active')}</h3>
                    </div>
                </div>
            </div>

            <div style={{...styles.card, borderTop: editId ? '4px solid #FFB547' : '4px solid #4318FF'}}>
                <div style={styles.cardHeader}>
                    <div style={styles.iconBox}><FontAwesomeIcon icon={editId ? faEdit : faPlus} /></div>
                    <h3 style={styles.cardTitle}>{editId ? `${t('edit_label')} #${editId}` : `${t('add_new_label')} ${getTabName()}`}</h3>
                    {editId && (
                        <button onClick={() => {setEditId(null); setFormData({}); setFile(null);}} style={styles.closeBtn}>
                            <FontAwesomeIcon icon={faTimes}/>
                        </button>
                    )}
                </div>
                
                <form onSubmit={handleSave} style={styles.form}>
                    <div style={styles.inputsContainer}>
                        {tab === 'slides' && (
                            <>
                                <input style={styles.input} placeholder={t('placeholder_title')} value={formData.title || ''} onChange={e => setFormData({...formData, title: e.target.value})} required />
                                <textarea style={styles.textarea} placeholder={t('placeholder_description')} value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} rows="2" />
                            </>
                        )}
                        {tab === 'categories' && (
    <>
        <input 
            style={styles.input} 
            placeholder={t('placeholder_name')} 
            value={formData.name || ''} 
            onChange={e => setFormData({...formData, name: e.target.value})} 
            required 
        />
        <input 
            style={styles.input} 
            placeholder={t('placeholder_icon_class')} 
            value={formData.icon || ''} 
            onChange={e => setFormData({...formData, icon: e.target.value})} 
        />
        {/* Ավելացրու սա SEO տեքստի համար */}
        <textarea 
            style={styles.textarea} 
            placeholder="SEO Տեքստ (բանալի բառերով հարուստ տեքստ)" 
            value={formData.seo_text || ''} 
            onChange={e => setFormData({...formData, seo_text: e.target.value})} 
            rows="4"
        />
    </>
)}
                        {tab === 'partners' && (
                            <input style={styles.input} placeholder={t('placeholder_partner_name')} value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} required />
                        )}
                    </div>

                    <label style={styles.fileUploadZone}>
                        <FontAwesomeIcon icon={faCloudUploadAlt} size="lg" style={{ color: '#4318FF', marginBottom: '8px' }} /><br/>
                        <span style={{fontSize: '13px', color: '#707EAE', fontWeight: '600'}}>
                            {file ? `${t('selected_label')}: ${file.name}` : t('click_to_upload')}
                        </span>
                        <input type="file" hidden onChange={e => setFile(e.target.files[0])} accept="image/*" />
                    </label>

                    <button type="submit" style={styles.saveBtn} disabled={loading}>
                        <FontAwesomeIcon icon={editId ? faSave : faPlus} style={{marginRight: '8px'}} /> 
                        {loading ? ` ${t('btn_processing')}` : (editId ? ` ${t('btn_update')}` : ` ${t('btn_save')}`)}
                    </button>
                </form>
            </div>

            <div style={{...styles.card, marginTop: '30px'}}>
                <div style={styles.tableWrapper}>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.thRow}>
                                <th style={styles.th}>ID</th>
                                <th style={styles.th}>{t('table_image')}</th>
                                <th style={styles.th}>{t('table_info')}</th>
                                <th style={{...styles.th, textAlign: 'center'}}>{t('table_actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((item, index) => (
                                <tr key={`${tab}-${item.id || index}-${index}`} style={styles.tr}>
                                    <td style={{...styles.td, color: '#707EAE'}}>#{item.id}</td>
                                    <td style={styles.td}>
                                        <div style={styles.imgWrapper}>
                                            {(item.image || item.logo) ? (
                                                <img 
    src={item.image || item.logo ? `${API_BASE}${item.image || item.logo}` : "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="} 
    style={styles.tableImg}
    alt={item.name || "item"} 
    loading="lazy"       // 1. Բեռնել միայն երբ տողը երևում է էկրանին
    decoding="async"     // 2. Ֆոնային վերծանում, որ սքրոլը չկախի
    onError={(e) => {
        e.target.onerror = null;
        e.target.src = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
    }}
/>
                                            ) : <div style={styles.noImg}><FontAwesomeIcon icon={faImage} /></div>}
                                        </div>
                                    </td>
                                    <td style={styles.td}>
                                        <div style={styles.itemTitle}>{item.name || item.title}</div>
                                        <div style={styles.itemSub}>{item.description || item.icon || ''}</div>
                                    </td>
                                    <td style={styles.tdActions}>
                                        <button onClick={() => handleEdit(item)} style={styles.editBtn}><FontAwesomeIcon icon={faEdit} /></button>
                                        <button onClick={() => handleDelete(item.id)} style={styles.deleteBtn}><FontAwesomeIcon icon={faTrash} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};



// ... Styles (նույնն են)
const styles = {
    container: { fontFamily: "'Plus Jakarta Sans', sans-serif" },
    analyticsRow: { display: 'flex', gap: '20px', marginBottom: '30px' },
    anaCard: { flex: 1, padding: '20px', borderRadius: '20px', color: '#fff', display: 'flex', alignItems: 'center', gap: '15px' },
    anaIconBox: { width: '45px', height: '45px', borderRadius: '12px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' },
    anaLabel: { fontSize: '11px', margin: 0, opacity: 0.8, textTransform: 'uppercase' },
    anaVal: { fontSize: '20px', margin: 0, fontWeight: '800' },
    card: { background: '#fff', borderRadius: '24px', padding: '25px', boxShadow: '0 10px 30px rgba(0,0,0,0.02)' },
    cardHeader: { display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px', position: 'relative' },
    iconBox: { width: '35px', height: '35px', borderRadius: '10px', background: '#F4F7FE', color: '#4318FF', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    cardTitle: { fontSize: '16px', fontWeight: '800', color: '#1B2559', margin: 0 },
    closeBtn: { position: 'absolute', right: 0, background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '18px' },
    form: { display: 'flex', flexDirection: 'column', gap: '15px' },
    inputsContainer: { display: 'flex', flexDirection: 'column', gap: '12px' },
    input: { padding: '12px 16px', borderRadius: '14px', border: '1px solid #E0E5F2', background: '#F4F7FE', outline: 'none', fontSize: '14px' },
    textarea: { padding: '12px 16px', borderRadius: '14px', border: '1px solid #E0E5F2', background: '#F4F7FE', outline: 'none', fontSize: '14px', resize: 'vertical' },
    fileUploadZone: { display: 'block', padding: '20px', border: '2px dashed #E0E5F2', borderRadius: '16px', textAlign: 'center', cursor: 'pointer', background: '#F4F7FE', transition: '0.3s' },
    saveBtn: { background: 'linear-gradient(135deg, #4318FF 0%, #5E37FF 100%)', color: '#fff', border: 'none', padding: '14px', borderRadius: '16px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 15px rgba(67, 24, 255, 0.2)' },
    tableWrapper: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse' },
    thRow: { borderBottom: '1px solid #F4F7FE' },
    th: { padding: '12px', textAlign: 'left', color: '#A3AED0', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase' },
    tr: { borderBottom: '1px solid #F4F7FE' },
    td: { padding: '12px', verticalAlign: 'middle' },
    imgWrapper: { width: '45px', height: '45px', borderRadius: '10px', overflow: 'hidden', border: '1px solid #F4F7FE' },
    tableImg: { width: '100%', height: '100%', objectFit: 'cover' },
    noImg: { width: '100%', height: '100%', background: '#F4F7FE', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#A3AED0' },
    itemTitle: { fontWeight: '700', color: '#1B2559', fontSize: '14px' },
    itemSub: { fontSize: '12px', color: '#A3AED0' },
    tdActions: { display: 'flex', gap: '10px', justifyContent: 'center', padding: '18px 0' },
    editBtn: { background: '#FFF7ED', color: '#FFB547', border: 'none', width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer' },
    deleteBtn: { background: '#FEE2E2', color: '#EF4444', border: 'none', width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer' }
};

export default AdminStandardSection;